import { useMemo, useState } from 'react';
import { Conversation } from '../lib/data';
import { TrendingUp, TrendingDown, Hash, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { DateRangePicker, DateRange } from './DateRangePicker';
import { MonthPicker } from './MonthPicker';

export type TrendingDateRangeType = 'today' | 'week' | 'month' | 'selectMonth' | 'custom';

interface TrendingTopicsProps {
  conversations: Conversation[];
}

interface TopicTrend {
  topic: string;
  count: number;
  percentage: number;
  trend: 'up' | 'down' | 'stable';
  sentimentImpact: 'positive' | 'negative' | 'neutral';
}

export function TrendingTopics({ conversations: conversations }: TrendingTopicsProps) {
  // Date range state management
  const [trendingSelectedRange, setTrendingSelectedRange] = useState<TrendingDateRangeType>('month');
  const [trendingCustomDateRange, setTrendingCustomDateRange] = useState<DateRange>({ from: undefined, to: undefined });
  const [trendingSelectedMonth, setTrendingSelectedMonth] = useState<{ month: number; year: number } | undefined>(undefined);

  // Date range formatting function
  const formatTrendingDateRange = () => {
    if (trendingSelectedRange === 'custom' && trendingCustomDateRange.from) {
      const from = trendingCustomDateRange.from.toLocaleDateString();
      const to = trendingCustomDateRange.to ? trendingCustomDateRange.to.toLocaleDateString() : 'Present';
      return `${from} - ${to}`;
    }
    if (trendingSelectedRange === 'selectMonth' && trendingSelectedMonth) {
      const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      return `${months[trendingSelectedMonth.month]} ${trendingSelectedMonth.year}`;
    }
    switch (trendingSelectedRange) {
      case 'today': return 'Today';
      case 'week': return 'Past 7 days';
      case 'month': return 'Past 30 days';
      default: return 'All time';
    }
  };

  // Filter conversations based on selected date range
  const filteredTrendingConversations = useMemo(() => {
    const now = new Date();
    
    return conversations.filter(conversation => {
      const conversationDate = new Date(conversation.lastUpdated);
      
      switch (trendingSelectedRange) {
        case 'today':
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return conversationDate >= today;
        
        case 'week':
          const weekAgo = new Date(now);
          weekAgo.setDate(weekAgo.getDate() - 7);
          return conversationDate >= weekAgo;
        
        case 'month':
          const monthAgo = new Date(now);
          monthAgo.setDate(monthAgo.getDate() - 30);
          return conversationDate >= monthAgo;
        
        case 'selectMonth':
          if (!trendingSelectedMonth) return true;
          return conversationDate.getMonth() === trendingSelectedMonth.month && 
                 conversationDate.getFullYear() === trendingSelectedMonth.year;
        
        case 'custom':
          if (!trendingCustomDateRange.from) return true;
          const fromDate = new Date(trendingCustomDateRange.from);
          fromDate.setHours(0, 0, 0, 0);
          const toDate = trendingCustomDateRange.to ? new Date(trendingCustomDateRange.to) : new Date();
          toDate.setHours(23, 59, 59, 999);
          return conversationDate >= fromDate && conversationDate <= toDate;
        
        default:
          return true;
      }
    });
  }, [conversations, trendingSelectedRange, trendingCustomDateRange, trendingSelectedMonth]);

  const topicTrends = useMemo(() => {
    // Count topic occurrences
    const topicCounts: { [key: string]: number } = {};
    const topicSentiments: { [key: string]: number[] } = {};

    filteredTrendingConversations.forEach(conversation => {
      conversation.topics.forEach(topic => {
        topicCounts[topic] = (topicCounts[topic] || 0) + 1;
        if (!topicSentiments[topic]) {
          topicSentiments[topic] = [];
        }
        // Convert sentiment to numeric for analysis
        const sentimentScore = getSentimentScore(conversation.sentiment);
        topicSentiments[topic].push(sentimentScore);
      });
    });

    // Calculate trends and sentiment impact
    const trends: TopicTrend[] = Object.entries(topicCounts).map(([topic, count]) => {
      const percentage = filteredTrendingConversations.length > 0 ? (count / filteredTrendingConversations.length) * 100 : 0;
      
      // Calculate average sentiment for this topic
      const avgSentiment = topicSentiments[topic].reduce((a, b) => a + b, 0) / topicSentiments[topic].length;
      
      // Simulate trend data (in a real app, you'd compare with historical data)
      const trendValue = Math.random();
      const trend: 'up' | 'down' | 'stable' = trendValue > 0.6 ? 'up' : trendValue < 0.4 ? 'down' : 'stable';
      
      const sentimentImpact: 'positive' | 'negative' | 'neutral' = 
        avgSentiment > 0.5 ? 'positive' : 
        avgSentiment < -0.5 ? 'negative' : 'neutral';

      return {
        topic,
        count,
        percentage,
        trend,
        sentimentImpact
      };
    });

    // Sort by count descending
    return trends.sort((a, b) => b.count - a.count);
  }, [filteredTrendingConversations]);

  function getSentimentScore(sentiment: string): number {
    const sentimentLevels = [
      'strong negative',
      'moderate negative', 
      'weak negative',
      'neutral',
      'weak positive',
      'moderate positive',
      'strong positive'
    ];
    const index = sentimentLevels.indexOf(sentiment);
    return index - 3; // Convert to -3 to +3 scale
  }

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-red-500" />;
      case 'down':
        return <TrendingDown className="w-4 h-4 text-green-500" />;
      default:
        return <Hash className="w-4 h-4 text-gray-400" />;
    }
  };

  const getSentimentBadgeColor = (impact: string) => {
    switch (impact) {
      case 'positive':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'negative':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Trending Topics</h3>
          <p className="text-gray-600 mt-1">Most frequently discussed topics for {formatTrendingDateRange()}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant={trendingSelectedRange === 'today' ? 'default' : 'outline'} size="sm" onClick={() => setTrendingSelectedRange('today')}>Today</Button>
          <Button variant={trendingSelectedRange === 'week' ? 'default' : 'outline'} size="sm" onClick={() => setTrendingSelectedRange('week')}>This Week</Button>
          <Button variant={trendingSelectedRange === 'month' ? 'default' : 'outline'} size="sm" onClick={() => setTrendingSelectedRange('month')}>This Month</Button>
          <Button variant={trendingSelectedRange === 'selectMonth' ? 'default' : 'outline'} size="sm" onClick={() => setTrendingSelectedRange('selectMonth')}>Select Month</Button>
          <Button variant={trendingSelectedRange === 'custom' ? 'default' : 'outline'} size="sm" onClick={() => setTrendingSelectedRange('custom')}>Custom</Button>
        </div>
      </div>

      {trendingSelectedRange === 'selectMonth' && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-4">
            <Calendar className="w-5 h-5 text-gray-400" />
            <MonthPicker value={trendingSelectedMonth} onChange={setTrendingSelectedMonth} placeholder="Select a month" />
          </div>
        </div>
      )}
      {trendingSelectedRange === 'custom' && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-4">
            <Calendar className="w-5 h-5 text-gray-400" />
            <DateRangePicker value={trendingCustomDateRange} onChange={setTrendingCustomDateRange} />
          </div>
        </div>
      )}

      <div className="text-xs text-gray-500 mb-6">
        Based on {filteredTrendingConversations.length} conversations
      </div>

      <div className="space-y-4">
        {topicTrends.slice(0, 6).map((topicTrend, index) => (
          <div key={topicTrend.topic} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 bg-white rounded-lg border border-gray-200">
                <span className="text-sm font-medium text-gray-600">
                  {index + 1}
                </span>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">{topicTrend.topic}</span>
                  {getTrendIcon(topicTrend.trend)}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-gray-600">
                    {topicTrend.count} conversations ({topicTrend.percentage.toFixed(1)}%)
                  </span>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs border ${getSentimentBadgeColor(topicTrend.sentimentImpact)}`}>
                    {topicTrend.sentimentImpact}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="w-24 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(topicTrend.percentage, 100)}%` }}
                />
              </div>
              <span className="text-sm font-medium text-gray-900 min-w-[3rem] text-right">
                {topicTrend.percentage.toFixed(1)}%
              </span>
            </div>
          </div>
        ))}
      </div>

      {topicTrends.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <Hash className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No topics to display</p>
        </div>
      )}
    </div>
  );
}